import { useCallback, useEffect, useState } from 'react';
import { fetchUrl } from '@utils/api';

/** Signed URLs (and parsed JSON) for the files a video project page shows. */

// Keys are S3 keys; a full or root-relative URL (the dev preview's local samples) is used as it is. A key that could
// not be signed throws (fetchUrl itself never does), so the page can say so instead of loading for ever.
const signed = async key => {
	if (!key) return null;
	if (/^(https?:|blob:|\/)/.test(key)) return key;
	const url = (await fetchUrl(`filelink?key=${encodeURIComponent(key)}`))?.url;
	if (!url) throw new Error(`no link for ${key}`);
	return url;
};

const json = async (url, init) => {
	const res = await fetch(url, init);
	if (!res.ok) throw new Error(`${res.status} for a result file`);
	return res.json();
};

// one file behind a key: { value, error, retry }; value is null while loading or with no key
const useFile = (key, load) => {
	const [state, setState] = useState({ value: null, error: null });
	const [attempt, setAttempt] = useState(0);
	useEffect(() => {
		setState({ value: null, error: null });
		if (!key) return undefined;
		let cancelled = false;
		load(key)
			.then(value => !cancelled && setState({ value, error: null }))
			.catch(error => {
				console.warn(`Could not load ${key}:`, error);
				if (!cancelled) setState({ value: null, error });
			});
		return () => {
			cancelled = true;
		};
	}, [key, attempt]); // load is one of this module's loaders, the same on every render
	const retry = useCallback(() => setAttempt(n => n + 1), []);
	return { ...state, retry };
};

const loadJson = key => signed(key).then(u => (u ? json(u) : null));

/** A new signed URL for an S3 key (the PDF report asks for one when the player's link no longer loads). */
export const signedUrl = signed;

/** A signed URL for an S3 key, with its error and a retry. */
export const useSignedUrlState = key => useFile(key, signed);

/** Parsed JSON behind an S3 key, with its error and a retry. */
export const useJsonFileState = key => useFile(key, loadJson);

/** A signed URL for an S3 key, or null while loading / when there is no key or it failed. */
export const useSignedUrl = key => useSignedUrlState(key).value;

/** Parsed JSON behind an S3 key. */
export const useJsonFile = key => useJsonFileState(key).value;

/**
 * A JSON file the running job replaces now and then (live_tracks.json): fetched again whenever `version` changes,
 * and the previous copy stays on screen until the new one is in.
 */
export const useLiveJsonFile = (key, version) => {
	const [doc, setDoc] = useState(null);
	useEffect(() => {
		if (!key) {
			setDoc(null);
			return undefined;
		}
		let cancelled = false;
		signed(key)
			.then(u => (u ? json(u, { cache: 'no-store' }) : null))
			.then(d => !cancelled && d && setDoc(d))
			.catch(err => console.warn(`Could not load ${key}:`, err));
		return () => {
			cancelled = true;
		};
	}, [key, version]);
	return doc;
};

/** The best browser-playable copy of the uploaded video: a compressed variant if there is one, else the raw file. */
export const sourceVideoKey = video => {
	const c = video?.compressed_paths;
	if (c && typeof c === 'object') {
		const first = Object.values(c).find(v => typeof v === 'string');
		if (first) return first;
	}
	return video?.s3_raw_path || null;
};
